import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-heading text-lg md:text-lg text-gold-400 font-semibold mb-2">{title}</h2>
    <div className="text-sm text-slate-300 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function Privacy() {
  return (
    <div className="min-h-screen relative" data-testid="privacy-page">
      <div className="grain" />
      <header className="fixed top-0 inset-x-0 z-30 glass">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo-link"><Logo /></Link>
          <Link to="/" data-testid="privacy-back-link"
            className="text-sm text-slate-400 hover:text-gold-400 transition-colors flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Torna al check-up
          </Link>
        </div>
      </header>

      <section className="relative pt-28 pb-24 px-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto card-dark rounded-2xl p-8 md:p-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3.5 py-1.5 text-xs text-gold-300 mb-6">
            <ShieldCheck className="h-3.5 w-3.5" /> Reg. UE 2016/679 (GDPR)
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mb-3">Informativa sulla Privacy</h1>
          <p className="text-slate-400 text-sm mb-10">
            Trattamento dei dati personali ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR)
            e del D.lgs. 196/2003 e s.m.i.
          </p>

          <Section title="1. Titolare del trattamento">
            <p>
              Titolare del trattamento è <strong className="text-white">DM SOLUTIONS GROUP SRLS</strong>,
              con sede legale e operativa in Via Panaro 85, Agliana (PT), P.IVA/C.F. 02132120474.
              Per qualsiasi richiesta è possibile scrivere a <span className="text-gold-400">info@dmsolutionsgroup.it</span>.
            </p>
          </Section>

          <Section title="2. Dati raccolti">
            <p>
              Nell'ambito del check-up energetico gratuito raccogliamo i dati che ci fornisci volontariamente
              (ragione sociale, email, telefono, settore, provincia) e i dati contenuti nelle bollette e nei
              documenti energetici che carichi (consumi, POD/PDR, costi, potenza), utilizzati per l'analisi
              energetica e la predisposizione di offerte e preventivi.
            </p>
          </Section>

          <Section title="3. Finalità del trattamento">
            <p>I dati sono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-5 space-y-1 marker:text-gold-400">
              <li>Eseguire l'analisi energetica e produrre la valutazione e il dossier richiesti;</li>
              <li>Ricontattarti per illustrare i risultati e fornire consulenza;</li>
              <li>Inviarti offerte, preventivi e comunicazioni relative ai nostri servizi, ora e in futuro;</li>
              <li>Adempiere agli obblighi di legge, contabili e fiscali.</li>
            </ul>
          </Section>

          <Section title="4. Base giuridica">
            <p>
              Il trattamento si fonda sul <strong className="text-white">consenso</strong> che presti al momento
              dell'invio del form (art. 6, par. 1, lett. a GDPR), sull'esecuzione di misure precontrattuali e
              contrattuali da te richieste (lett. b) e sull'adempimento di obblighi legali (lett. c).
            </p>
          </Section>

          <Section title="5. Conservazione dei dati">
            <p>
              I dati sono conservati per il tempo necessario alle finalità indicate e, in ogni caso, fino a tua
              esplicita richiesta di rimozione. I dati necessari ad adempimenti di legge sono conservati per i
              termini di legge previsti.
            </p>
          </Section>

          <Section title="6. Comunicazione dei dati">
            <p>
              I dati potranno essere trattati da personale autorizzato e da fornitori di servizi (es. fornitori
              di energia partner, consulenti, servizi IT) nominati responsabili del trattamento, esclusivamente
              per le finalità sopra indicate. I dati non sono oggetto di diffusione.
            </p>
          </Section>

          <Section title="7. I tuoi diritti">
            <p>
              In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 del GDPR: accesso,
              rettifica, cancellazione, limitazione, portabilità, opposizione e <strong className="text-white">revoca del consenso</strong>.
            </p>
          </Section>

          <div className="rounded-xl border border-gold-500/30 bg-gold-500/5 p-5 flex items-start gap-3" data-testid="privacy-pec-box">
            <Mail className="h-5 w-5 text-gold-400 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-200 leading-relaxed">
              <strong className="text-white">Rimozione dai nostri sistemi.</strong> Per essere rimosso e non
              ricevere più alcuna comunicazione è sufficiente inviare una PEC (o email) all'indirizzo{" "}
              <span className="text-gold-400">info@dmsolutionsgroup.it</span>. Provvederemo alla cancellazione
              dei tuoi dati e alla cessazione di ogni contatto.
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10">
            <Link to="/" data-testid="privacy-footer-back"
              className="inline-flex items-center gap-1.5 text-sm text-gold-400 hover:text-gold-300 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Torna al check-up gratuito
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
